import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "Reset link sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background afha-grain flex flex-col" data-testid="forgot-password-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[68px] flex items-center">
          <Logo />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-border rounded-xl p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="h-14 w-14 rounded-full bg-patriot/10 flex items-center justify-center mx-auto mb-5">
                <MailCheck className="h-7 w-7 text-patriot" />
              </div>
              <h1 className="font-display text-2xl text-navy">Check your email</h1>
              <p className="mt-3 text-muted-foreground text-sm">
                If an account exists for <span className="font-semibold text-navy">{email}</span>, we've sent a link
                to reset your password. The link expires in 1 hour.
              </p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-patriot hover:underline" data-testid="back-to-login">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl text-navy tracking-tight">Forgot your password?</h1>
              <p className="mt-2 text-muted-foreground text-sm">Enter your email and we'll send you a link to reset it.</p>
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email" className="text-navy">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="mt-1.5 h-11" placeholder="you@example.com" />
                </div>
                <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full h-11 rounded-full bg-navy hover:bg-navy-dark text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-patriot hover:underline" data-testid="back-to-login">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
